import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { LayoutUtilsService, QueryParamsModel } from '../../../../../../core/_base/crud';
import { fromEvent, merge } from 'rxjs';
import { AppState } from '../../../../../../core/reducers';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { CategoryDescriptionEditComponent } from '../category-description-edit/category-description-edit.component';
import { CategoryDescriptionDataSource } from '../../../../../../core/lists/_data-sources/category-description.datasource';
import {
  CategoryDescriptionOnServerDeleted,
  CategoryDescriptionPageRequested,
} from '../../../../../../core/lists/_actions/category-description.actions';
import { Category } from '../../../../../../core/lists';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { RelationshipCategoryService } from '../../../../../../core/lists/_services/relationship-category.service';

@Component({
  selector: 'kt-category-description-list',
  templateUrl: './category-description-list.component.html',
  styleUrls: ['./category-description-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryDescriptionListComponent implements OnInit, OnDestroy {
  dataSource: CategoryDescriptionDataSource;
  displayedColumns = ['category.entityType', 'category.name', 'descriptionType', 'actions'];
  searchBarColumns = ['searchType', 'searchCategory', 'searchDescription', 'searchActions'];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchTypeInput = '';
  searchDescriptionInput = '';
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;
  getCategoryAutocomplete =
    this.relationCategoryService.searchAutocompleteRelationshipCategory.bind(
      this.relationCategoryService,
    );
  @ViewChild('searchCategoryAutocomplete') searchCategoryAutocomplete: GeneralAutocompleteComponent;
  typesList = [
    {
      label: 'Doctor',
      value: 'DOCTOR',
    },
    {
      label: 'Patient',
      value: 'PATIENT',
    },
    {
      label: 'Facility',
      value: 'FACILITY',
    },
    {
      label: 'Contact',
      value: 'CONTACT',
    },
    {
      label: 'Affiliation',
      value: 'AFFILIATION_GROUP',
    },
    {
      label: 'Client',
      value: 'CLIENT',
    },
  ];

  constructor(
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private relationCategoryService: RelationshipCategoryService,
    private navigationService: NavigationService,
    private layoutUtilsService: LayoutUtilsService,
    private fb: FormBuilder,
    private store: Store<AppState>,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadCategoryDescriptionList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadCategoryDescriptionList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new CategoryDescriptionDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadCategoryDescriptionList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadCategoryDescriptionList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new CategoryDescriptionPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchDescriptionInput = appliedFilters.description;
        this.searchTypeInput = appliedFilters.type;
        if (this.searchCategoryAutocomplete)
          this.searchCategoryAutocomplete.value.id = appliedFilters.category;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'id';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.description = this.searchDescriptionInput;
      filter.type = this.searchTypeInput;
      filter.category = this.searchCategoryAutocomplete?.value?.name;
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'id';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deleteCategoryDescription(_item: { id: any }) {
    const _title = 'Delete Category Description';
    const _description = 'ARe you sure you want to delete Description?';
    const _waitDescription = 'Deleting...';

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new CategoryDescriptionOnServerDeleted({ id: _item.id }));
      this.loadCategoryDescriptionList();
    });
  }

  editCategoryDescription(item: any) {
    const dialogRef = this.dialog.open(CategoryDescriptionEditComponent, {
      data: { mode: 'UPDATE', item },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadCategoryDescriptionList();
    });
  }

  createCategoryDescription() {
    const dialogRef = this.dialog.open(CategoryDescriptionEditComponent, {
      data: { mode: 'CREATE' },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadCategoryDescriptionList();
    });
  }

  formatCategoryOption(category: Category): string {
    return category?.name || '';
  }

  reloadCategoryDescriptionList(): void {
    this.paginator.pageIndex = 0;
    this.loadCategoryDescriptionList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchDescriptionInput = '';
    this.searchTypeInput = '';
    this.searchCategoryAutocomplete?.setValue(null);
    this.searchCategoryAutocomplete?.updateValueAndValidity();
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadCategoryDescriptionList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadCategoryDescriptionList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadCategoryDescriptionList();
  }
}
